// The step strip the Statutory tab's filings run on, in the same shape as
// the TDS stepper in Startup Khata: a horizontal track of numbered steps,
// each with a title, a line of state underneath, and its actions as plain
// underlined links.
//
// One component for PF and ESI rather than one each. They had grown
// near-identical copies of the marker, the note and the button CSS, and two
// copies of a look is how the two halves of one tab drift apart.
//
// It decides NOTHING about progress. Every state is handed in, derived from
// what the portals actually report. If a step looks wrong, the fact behind
// it is what to go and look at.
import React from "react";

export type StepState = "done" | "current" | "blocked" | "locked";

export interface StatutoryStep {
  key: string;
  title: string;
  state: StepState;
  /** Something short beside the title — a due date, a count. A node, not a
   *  string: TDS hands over a DueChip whose colour carries the meaning
   *  (overdue red, due-soon amber), and flattening that to text would throw
   *  the meaning away. Use StepBadge for a plain one. */
  badge?: React.ReactNode;
  note?: React.ReactNode;
  action?: React.ReactNode;
}

export const STEP_CSS = `
.tl-steps-wrap { position: relative; }
.tl-steps {
  display: flex; align-items: flex-start;
  overflow-x: auto; overflow-y: hidden;
  padding: 2px 0 10px;
  scrollbar-width: thin;
}
.tl-steps::-webkit-scrollbar { height: 8px; }
.tl-steps::-webkit-scrollbar-thumb {
  background: var(--gt-border); border-radius: 999px;
}
.tl-steps::-webkit-scrollbar-track { background: transparent; }

.tl-step { flex: 0 0 auto; min-width: 172px; max-width: 260px; padding-right: 4px; }
.tl-step-head { display: flex; align-items: center; gap: 8px; }
.tl-step-title { font-size: 13.5px; font-weight: 600; line-height: 1.2; }

/* Indented to the title, not to the marker: the number is a position in the
   track, and hanging the text under it instead reads as a bullet list. */
.tl-step-body { padding-left: 34px; }

.tl-step-dot {
  width: 26px; height: 26px; border-radius: 999px; flex-shrink: 0;
  font-size: 11.5px; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}
.tl-step-dot.is-done    { background: var(--gt-primary); color: #fff; }
.tl-step-dot.is-current { background: #fff; color: var(--gt-primary);
                          box-shadow: inset 0 0 0 2px var(--gt-primary); }
.tl-step-dot.is-blocked { background: #fef3c7; color: #92400e;
                          box-shadow: inset 0 0 0 1px #fcd34d; }
.tl-step-dot.is-locked  { background: var(--gt-surface-2); color: var(--gt-text-tertiary);
                          box-shadow: inset 0 0 0 1px var(--gt-border); }

/* The dash between two steps, level with the middle of the markers. */
.tl-step-link {
  flex: 0 0 28px; margin: 13px 8px 0;
  border-top: 1px dashed var(--gt-border);
}

.tl-step-badge {
  font-size: 10.5px; font-weight: 600; white-space: nowrap;
  padding: 2px 7px; border-radius: 999px;
  background: var(--gt-surface-2); color: var(--gt-text-secondary);
}

.tl-step-note {
  font-size: 11.5px; line-height: 1.35; margin: 3px 0 0;
  color: var(--gt-text-tertiary);
}
.tl-step-note.is-warn { color: #92400e; }

.tl-step-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

/* Underlined text, no box. The underline is what says "clickable"; a filled
   button at every step competes with the numbered markers, which are
   already carrying the where-am-I job. */
.pf-step-btn {
  margin-top: 5px; padding: 0; background: none; border: 0;
  font-size: 12px; font-weight: 600; line-height: 1.3;
  color: var(--gt-primary);
  text-decoration: underline; text-underline-offset: 3px;
  cursor: pointer; white-space: nowrap;
  transition: color 120ms;
}
.pf-step-btn:hover:not(:disabled) { color: var(--gt-primary-hover); }
.pf-step-btn:focus-visible { outline: 2px solid var(--gt-primary); outline-offset: 2px; border-radius: 3px; }
/* The only disabled case is "a portal run is already going" — two runs would
   fight over the same window. */
.pf-step-btn:disabled { color: var(--gt-text-disabled); cursor: not-allowed; text-decoration: none; }
/* A step whose turn has not come. Greyed so the eye skips it, but still a
   live link: the order is advice, not a lock — re-running a step, or doing
   one early on purpose, is sometimes exactly what is needed, and the
   extension checks the portal's own state before it submits anything. */
.pf-step-btn.is-waiting { color: var(--gt-text-disabled); font-weight: 500; }
.pf-step-btn.is-waiting:hover:not(:disabled) { color: var(--gt-text-2, #64748b); }

/* The portal sign-in. A filled box rather than the steps' underline, because
   it is not one of them: the numbered steps are the monthly cycle, and this is
   what you reach for when the cycle cannot get past the portal. It also sits
   in the card header, away from the markers it would otherwise compete with.

   Named gt- on purpose. global-theme.css gives every bare button the soft
   theme look, including color: var(--gt-text-primary), at a specificity of
   (0,6,1):

     .gt-themed button:not([class*="bg-"]):not([class*="gt-"])
                      :not([class*="rounded"]):not([class*="cm-"]):not(:disabled)

   A plain class is (0,1,0) and loses, so the dark background landed and the
   white text did not — a dark green box with near-black writing in it. That
   selector excludes any class containing "gt-", which is the stylesheet's own
   way of saying "this one styles itself". */
.gt-portal-btn {
  margin: 0; padding: 6px 12px;
  background: var(--gt-primary); border: 1px solid var(--gt-primary);
  border-radius: 6px;
  font-family: var(--gt-font-display);
  font-size: 12px; font-weight: 600; line-height: 1.2;
  color: var(--gt-on-primary, #fff); text-decoration: none;
  cursor: pointer; white-space: nowrap;
  transition: background 120ms, border-color 120ms;
}
.gt-portal-btn:hover:not(:disabled) {
  background: var(--gt-primary-hover); border-color: var(--gt-primary-hover);
}
.gt-portal-btn:focus-visible { outline: 2px solid var(--gt-primary); outline-offset: 2px; }
/* Disabled means a portal run is already going — two would fight over the
   same window. */
.gt-portal-btn:disabled {
  background: var(--gt-text-disabled); border-color: var(--gt-text-disabled);
  color: var(--gt-on-primary, #fff);
  cursor: not-allowed;
}

.tl-steps-arrow {
  position: absolute; bottom: 4px; width: 20px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 4px; font-size: 11px; line-height: 1;
  color: var(--gt-text-tertiary); background: var(--gt-surface-0);
  border: 1px solid var(--gt-border); cursor: pointer;
}
.tl-steps-arrow:hover { color: var(--gt-text-secondary); }
.tl-steps-arrow.is-start { left: 0; }
.tl-steps-arrow.is-end { right: 0; }
`;

/** The plain pill. Callers with their own (TDS's DueChip) pass that instead. */
export function StepBadge({ children }: { children: React.ReactNode }) {
  return <span className="tl-step-badge">{children}</span>;
}

export function StepNote({ children, tone }: { children: React.ReactNode; tone?: "warn" }) {
  return <p className={`tl-step-note${tone === "warn" ? " is-warn" : ""}`}>{children}</p>;
}

function Marker({ n, state }: { n: number; state: StepState }) {
  return (
    <span aria-hidden className={`tl-step-dot is-${state}`}>
      {state === "done" ? "✓" : state === "blocked" ? "!" : n}
    </span>
  );
}

export default function StatutorySteps({ steps }: { steps: StatutoryStep[] }) {
  const track = React.useRef<HTMLDivElement>(null);
  // Arrows only when the track is actually wider than its box. Five steps
  // fit on a desktop and six do not, so this cannot be decided by counting.
  const [overflowing, setOverflowing] = React.useState(false);

  React.useEffect(() => {
    const el = track.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [steps.length]);

  const scrollBy = (amount: number) =>
    track.current?.scrollBy({ left: amount, behavior: "smooth" });

  return (
    <div className="tl-steps-wrap">
      <div className="tl-steps" ref={track}>
        {steps.map((step, i) => (
          <React.Fragment key={step.key}>
            {i > 0 && <span aria-hidden className="tl-step-link" />}
            <div className="tl-step">
              <div className="tl-step-head">
                <Marker n={i + 1} state={step.state} />
                <span className="tl-step-title">{step.title}</span>
                {step.badge}
              </div>
              <div className="tl-step-body">
                {step.note}
                {step.action && <div className="tl-step-actions">{step.action}</div>}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>
      {overflowing && (
        <>
          <button type="button" aria-label="Earlier steps"
            className="tl-steps-arrow is-start" onClick={() => scrollBy(-260)}>◀</button>
          <button type="button" aria-label="Later steps"
            className="tl-steps-arrow is-end" onClick={() => scrollBy(260)}>▶</button>
        </>
      )}
    </div>
  );
}
